import { saveItem, getItem, saveObject, getObject, removeItem } from '../../utils/storage';
import { SECURITY_STORAGE_KEYS } from '../../utils/constants';

// Salt makes it harder to reverse the hash if localStorage is read directly
const PIN_SALT = 'car_alert_pin_salt_2026';

/**
 * Hash a PIN using the Web Crypto API (SHA-256).
 * @param {string} pin
 * @returns {Promise<string>} hex digest
 */
const hashPin = async (pin) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(pin + PIN_SALT);
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
};

/**
 * Save a hashed PIN to storage.
 * @param {string} pin  Plain-text 6-digit PIN
 */
export const savePin = async (pin) => {
  const hash = await hashPin(pin);
  await saveItem(SECURITY_STORAGE_KEYS.PIN_HASH, hash);
};

/**
 * Verify a PIN against the stored hash.
 * @param {string} pin  Plain-text PIN to verify
 * @returns {Promise<boolean>}
 */
export const verifyPin = async (pin) => {
  const storedHash = await getItem(SECURITY_STORAGE_KEYS.PIN_HASH);
  if (!storedHash) return false;
  const hash = await hashPin(pin);
  return hash === storedHash;
};

/**
 * Check whether a PIN has been configured.
 * @returns {Promise<boolean>}
 */
export const hasPinConfigured = async () => {
  const hash = await getItem(SECURITY_STORAGE_KEYS.PIN_HASH);
  return !!hash;
};

/**
 * Remove the stored PIN.
 */
export const removePin = async () => {
  await removeItem(SECURITY_STORAGE_KEYS.PIN_HASH);
};

/**
 * Persist a face descriptor (Float32Array) to localStorage.
 * @param {Float32Array} descriptor
 */
export const saveFaceDescriptor = async (descriptor) => {
  await saveObject(SECURITY_STORAGE_KEYS.FACE_DATA, {
    descriptor: Array.from(descriptor),
  });
};

/**
 * Retrieve the stored face descriptor.
 * @returns {Promise<Float32Array|null>}
 */
export const getFaceDescriptor = async () => {
  const data = await getObject(SECURITY_STORAGE_KEYS.FACE_DATA);
  if (!data || !data.descriptor) return null;
  return new Float32Array(data.descriptor);
};

/**
 * Check whether face data has been registered.
 * @returns {Promise<boolean>}
 */
export const hasFaceConfigured = async () => {
  const data = await getObject(SECURITY_STORAGE_KEYS.FACE_DATA);
  return !!(data && data.descriptor);
};

/**
 * Remove the stored face descriptor.
 */
export const removeFaceData = async () => {
  await removeItem(SECURITY_STORAGE_KEYS.FACE_DATA);
};

/**
 * Persist the preferred inactivity lock timeout (milliseconds).
 * @param {number} ms  Use 0 to disable auto-lock.
 */
export const saveInactivityTimeout = async (ms) => {
  await saveItem(SECURITY_STORAGE_KEYS.INACTIVITY_TIMEOUT, String(ms));
};

/**
 * Retrieve the preferred inactivity lock timeout.
 * Defaults to 5 minutes (300 000 ms) when not set.
 * @returns {Promise<number>}
 */
export const getInactivityTimeout = async () => {
  const stored = await getItem(SECURITY_STORAGE_KEYS.INACTIVITY_TIMEOUT);
  return stored !== null ? Number(stored) : 300000;
};
